import type { CharacterDraft, CharacterDraftMeta, WorldDraft, WorldSnapshot } from '../../domain/types.js';
import { buildWorldSnapshot, generateLocalDevices } from './buildSnapshot.js';
import { buildLaunchRoster, type CharacterBodySnapshot } from './characterHandoff.js';
import { confirmAllAnswers } from './draftReducer.js';
import { clearCharacterBodies, clearDraftFromStorage, saveLaunchHandoff, saveSnapshotToStorage } from './draftStorage.js';
import type { Npc } from '../../types.js';

/** Bridge v2 CharacterDraft to v1 handoff meta until characterHandoff migrates (Worker D). */
function asHandoffMeta(
  character: CharacterDraft,
  bodies: Record<string, CharacterBodySnapshot>,
): CharacterDraftMeta {
  const sidecar = bodies[character.draftCharacterId];
  return {
    draftCharacterId: character.draftCharacterId,
    schemaVersion: character.schemaVersion,
    firstName: character.basic.firstName,
    lastInitial: character.basic.lastInitial,
    species: sidecar?.species ?? 'Human',
    sex: sidecar?.sex ?? 'M',
    ageRange: sidecar?.ageRange ?? 'Adult',
    status: character.status,
    source: character.source,
    updatedAt: character.updatedAt,
  };
}

export interface LaunchPayload {
  snapshot: WorldSnapshot;
  characters: CharacterDraftMeta[];
  roster: Npc[];
}

export async function buildLaunchPayload(
  draft: WorldDraft,
  characterBodies: Record<string, CharacterBodySnapshot>,
): Promise<{ ok: true; payload: LaunchPayload } | { ok: false; error: string }> {
  const devices = generateLocalDevices(draft.draftId);
  const confirmed = confirmAllAnswers(draft);
  const built = buildWorldSnapshot(confirmed, devices);
  if (!built.ok) return built;
  const chars = confirmed.characterDrafts.filter((c) => c.status !== 'removed');
  const handoffChars = chars.map((c) => asHandoffMeta(c, characterBodies));
  const roster = buildLaunchRoster(handoffChars, characterBodies, built.snapshot, confirmed.draftId);
  return { ok: true, payload: { snapshot: built.snapshot, characters: handoffChars, roster } };
}

export function persistLaunch(payload: LaunchPayload, draftId: string): void {
  saveSnapshotToStorage(payload.snapshot);
  saveLaunchHandoff({
    schemaVersion: '1.0.0',
    snapshot: payload.snapshot,
    characters: payload.characters,
    roster: payload.roster,
    savedAt: new Date().toISOString(),
  });
  clearDraftFromStorage();
  clearCharacterBodies(draftId);
}

export function pause(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
